import { useState, useEffect, useRef } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { addBusiness } from "../services/businessService";
import { sendOtp, verifyOtp, getMobileVerificationSettings } from "../services/otpService";
import { CATEGORIES } from "../constants/categories";
import { getCategories } from "../services/categoryService";
import { getCities } from "../services/cityService";
import { IconUpload, IconPin } from "../components/Icons";

const RESEND_COOLDOWN = 60; // seconds before resend is allowed

const AddBusiness = () => {
  const routerLocation = useRouterLocation();
  const isManualMode = routerLocation.state?.manualLocation === true;

  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    businessName: "",
    category: "Electronics",
    services: "",
    city: isManualMode ? "" : "Detecting...",
    ownerName: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    description: ""
  });

  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [coords, setCoords] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Category list: hardcoded fallback, replaced with API data when available
  const [categoryList, setCategoryList] = useState(CATEGORIES);

  // City list: loaded from API, empty array until loaded
  const [cityList, setCityList] = useState([]);

  // OTP settings fetched from backend
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpMandatory, setOtpMandatory] = useState(false);

  // OTP verification state
  const [mobileVerified, setMobileVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const countdownRef = useRef(null);


  // Fetch OTP settings on mount
  useEffect(() => {
    getMobileVerificationSettings()
      .then((data) => {
        setOtpEnabled(data.otpEnabled);
        setOtpMandatory(data.otpMandatory);
      })
      .catch(() => {
        // If settings fetch fails, default to no OTP (safe fallback)
      });
  }, []);

  // Load categories from API; fall back to the hardcoded constant on failure
  useEffect(() => {
    getCategories()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const names = data.filter((c) => c.active).map((c) => c.name);
          if (names.length > 0) {
            setCategoryList(names);
            setForm((prev) => ({
              ...prev,
              category: names.includes(prev.category) ? prev.category : names[0]
            }));
          }
        }
      })
      .catch(() => {
        // API unavailable — keep the hardcoded fallback already in state
      });
  }, []);

  // Load cities from API; if it fails the dropdown stays empty (manual mode user sees no options)
  useEffect(() => {
    getCities()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setCityList(data);
      })
      .catch(() => {});
  }, []);

  // Auto location + city detection — skipped when user chose manual entry
  useEffect(() => {
    if (isManualMode) {
      setLocationLoading(false);
      return;
    }

    if (!navigator.geolocation) {
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        setCoords({ lat, lng });
        setLocationAllowed(true);
        setLocationLoading(false);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );

          const data = await res.json();

          let detectedCity =
            data.address?.state_district ||
            data.address?.county ||
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.municipality ||
            "";

          if (detectedCity.includes("District")) {
            detectedCity = detectedCity.replace(" District", "");
          }

          if (detectedCity) {
            setForm((prev) => ({ ...prev, city: detectedCity }));
          }

          detectedCity = detectedCity
            .replace(/district/i, "")
            .replace(/-/g, "")
            .trim();

        } catch (err) {
          console.log("City detection failed:", err);
        }
      },
      (err) => {
        console.log("Location permission denied:", err);
        setLocationAllowed(false);
        setLocationLoading(false);
        setLocationError("Geolocation failed. Please enable location access or enter location manually.");
      }
    );
  }, []);

  // Countdown timer for OTP resend cooldown
  useEffect(() => {
    if (resendCountdown <= 0) return;

    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [resendCountdown]);

  // =========================
  // VALIDATION HELPERS
  // =========================

  const normalizePhone = (phone) => {
    let cleaned = phone.replace(/[\s\-\.]/g, "");
    if (cleaned.startsWith("+91")) {
      cleaned = cleaned.slice(3);
    } else if (cleaned.startsWith("91") && cleaned.length === 12) {
      cleaned = cleaned.slice(2);
    }
    return cleaned;
  };

  const normalizeWebsite = (website) => {
    const trimmed = website.trim();
    if (!trimmed) return "";
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      return "https://" + trimmed;
    }
    return trimmed;
  };

  const validate = () => {
    const errs = {};

    const bName = form.businessName.trim();
    if (!bName) {
      errs.businessName = "Business name is required";
    } else if (bName.length < 3) {
      errs.businessName = "Business name must be at least 3 characters";
    } else if (bName.length > 100) {
      errs.businessName = "Business name cannot exceed 100 characters";
    }

    const oName = form.ownerName.trim();
    if (!oName) {
      errs.ownerName = "Owner name is required";
    } else if (oName.length < 2) {
      errs.ownerName = "Owner name must be at least 2 characters";
    } else if (oName.length > 60) {
      errs.ownerName = "Owner name cannot exceed 60 characters";
    }

    const normalizedPhone = normalizePhone(form.phone.trim());
    if (!form.phone.trim()) {
      errs.phone = "Phone number is required";
    } else if (!/^[6-9][0-9]{9}$/.test(normalizedPhone)) {
      errs.phone = "Enter a valid 10-digit Indian mobile number";
    } else if (otpEnabled && otpMandatory && !mobileVerified) {
      // Block submission if OTP is mandatory and phone is not verified
      errs.phone = "Please verify your mobile number before submitting.";
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = "Enter a valid email address";
    }

    if (form.website.trim()) {
      const normalized = normalizeWebsite(form.website);
      try {
        new URL(normalized);
      } catch {
        errs.website = "Enter a valid website (e.g. example.com)";
      }
    }

    const services = form.services.trim();
    if (!services) {
      errs.services = "Please describe your services";
    } else if (services.length > 300) {
      errs.services = "Services description cannot exceed 300 characters";
    }

    if (form.description.trim().length > 1000) {
      errs.description = "Description cannot exceed 1000 characters";
    }

    if (form.address.trim().length > 300) {
      errs.address = "Address cannot exceed 300 characters";
    }

    return errs;
  };

  // =========================
  // FORM HANDLING
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // When the phone number is edited, reset the verification state
  const handlePhoneChange = (e) => {
    handleChange(e);
    if (mobileVerified || otpSent) {
      setMobileVerified(false);
      setOtpSent(false);
      setOtpValue("");
      setOtpError("");
      clearInterval(countdownRef.current);
      setResendCountdown(0);
    }
  };

  // =========================
  // OTP HANDLING
  // =========================
  const getCleanPhone = () => normalizePhone(form.phone.trim());

  const handleSendOtp = async () => {
    const cleanPhone = getCleanPhone();
    if (!/^[6-9][0-9]{9}$/.test(cleanPhone)) {
      setOtpError("Enter a valid 10-digit Indian mobile number before verifying.");
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError("");
      await sendOtp(cleanPhone);
      setOtpSent(true);
      setResendCountdown(RESEND_COOLDOWN);
      toast.success("OTP sent! Check your phone.");
    } catch (err) {
      setOtpError(err.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || !/^\d{6}$/.test(otpValue)) {
      setOtpError("Enter the 6-digit OTP sent to your phone.");
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError("");
      await verifyOtp(getCleanPhone(), otpValue);
      setMobileVerified(true);
      setOtpSent(false);
      setOtpValue("");
      clearInterval(countdownRef.current);
      setResendCountdown(0);
      // Clear any phone validation error that was blocking submission
      setErrors((prev) => ({ ...prev, phone: "" }));
      toast.success("Mobile number verified!");
    } catch (err) {
      setOtpError(err.response?.data?.message || "Verification failed. Please check the OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  // =========================
  // IMAGE HANDLING
  // =========================

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const maxWidth = 1200;
        const maxHeight = 1200;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const scaleFactor = Math.min(maxWidth / width, maxHeight / height);
          width = width * scaleFactor;
          height = height * scaleFactor;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error("Failed to compress image");
              resolve(file);
              return;
            }
            const compressedFile = new File([blob], file.name || "compressed.jpg", {
              type: "image/jpeg",
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          0.75
        );
      };
    });
  };

  const handleImageFile = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length + images.length > 3) {
      toast.error("Maximum 3 images allowed");
      return;
    }

    const validFiles = files.filter(file => file.type.startsWith("image/"));

    setImageLoading(true);
    try {
      const compressedImages = await Promise.all(
        validFiles.map(file => compressImage(file))
      );

      const newPreviews = compressedImages.map(file => URL.createObjectURL(file));

      setImages(prev => [...prev, ...compressedImages]);
      setPreviews(prev => [...prev, ...newPreviews]);
    } finally {
      setImageLoading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...previews];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setImages(newImages);
    setPreviews(newPreviews);
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    let submitCoords = coords;

    if (!submitCoords && !isManualMode) {
      toast.error("Location access is required ❌");
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isManualMode && !form.city) {
      toast.error("Please select your district to continue.");
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      // Manual mode: resolve coordinates from the predefined city dropdown
      if (!submitCoords) {
        const cityEntry = cityList.find((c) => c.name === form.city);
        if (!cityEntry) {
          toast.error("Please select your district to continue.");
          return;
        }
        submitCoords = { lat: cityEntry.lat, lng: cityEntry.lng };
      }

      const formData = new FormData();

      formData.append("businessName", form.businessName.trim());
      formData.append("category", form.category);
      formData.append("services", form.services.trim());
      formData.append("city", form.city);
      formData.append("ownerName", form.ownerName.trim());
      formData.append("phone", normalizePhone(form.phone.trim()));
      formData.append("email", form.email.trim());
      formData.append("website", normalizeWebsite(form.website));
      formData.append("address", form.address.trim());
      formData.append("description", form.description.trim());

      images.forEach((img) => {
        formData.append("images", img);
      });

      formData.append(
        "location",
        JSON.stringify({
          type: "Point",
          coordinates: [submitCoords.lng, submitCoords.lat],
        })
      );

      formData.append("locationSource", isManualMode ? "manual" : "gps");

      await addBusiness(formData);
      setSubmitted(true);

    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message ||
        "Error registering business ❌"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAnother = () => {
    setSubmitted(false);
    setForm({
      businessName: "",
      category: categoryList[0] || "Electronics",
      services: "",
      city: isManualMode ? "" : (coords ? form.city : "Detecting..."),
      ownerName: "",
      phone: "",
      email: "",
      website: "",
      address: "",
      description: ""
    });
    setImages([]);
    setPreviews([]);
    setErrors({});
    setMobileVerified(false);
    setOtpSent(false);
    setOtpValue("");
    setOtpError("");
  };

  // =========================
  // BLOCK UI IF NO LOCATION
  // =========================
  if (!locationAllowed && !isManualMode) {
    return (
      <div className="wireframe-main">
        <h2 className="form-title">
          {locationLoading ? "Detecting Location..." : "Location Required"}
        </h2>
        <div className="status-message">
          <p style={{ margin: 0 }}>
            {locationLoading
              ? "Detecting your location, please wait..."
              : locationError || "Please allow location access to register your business."}
          </p>
          {!locationLoading && (
            <p style={{ margin: "8px 0 0", fontSize: "0.85rem" }}>
              Go to your browser settings → Site settings → Location → Allow, then refresh the page.
            </p>
          )}
        </div>
      </div>
    );
  }

  // =========================
  // SUCCESS SCREEN
  // =========================
  if (submitted) {
    return (
      <div className="wireframe-main">
        <div className="success-screen">
          <div className="success-check">&#10003;</div>
          <h2 className="form-title">Business Registered!</h2>
          <p className="success-msg">
            Your listing is under review and will be visible to nearby customers shortly.
          </p>
          <button className="search-btn" onClick={handleRegisterAnother}>
            Register Another Business
          </button>
        </div>
      </div>
    );
  }

  // =========================
  // UI
  // =========================
  return (
    <div className="wireframe-main">
      <Helmet>
        <title>Register Your Business – BusinessNearMe</title>
        <meta name="description" content="Add your local business to BusinessNearMe. Reach customers in Tamil Nadu by listing your shop, service or restaurant for free." />
        <link rel="canonical" href="https://businessnearme.in/add-business" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <h2 className="form-title">Register Your Business</h2>

      <form onSubmit={handleSubmit} className="product-form">

        {/* Business Name */}
        <div className="form-group">
          <label htmlFor="businessName">Business Name</label>
          <input
            id="businessName"
            name="businessName"
            value={form.businessName}
            onChange={handleChange}
            className={`frm-input ${errors.businessName ? "error" : ""}`}
          />
          {errors.businessName && <span className="field-error">{errors.businessName}</span>}
        </div>

        {/* Category */}
        <div className="form-group">
          <label htmlFor="category">Business Category</label>
          <select
            id="category"
            name="category"
            value={form.category}
            onChange={handleChange}
            className="location-select"
          >
            {categoryList.map((cat) => (
              <option key={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Services */}
        <div className="form-group">
          <label htmlFor="services">Service Offering</label>
          <textarea
            id="services"
            name="services"
            value={form.services}
            onChange={handleChange}
            placeholder="Example: AC Repair, TV Service, Mobile Repair"
            className={`frm-input ${errors.services ? "error" : ""}`}
          />
          {errors.services && <span className="field-error">{errors.services}</span>}
        </div>

        {/* City — dropdown in manual mode, free text in GPS mode */}
        <div className="form-group">
          <label htmlFor="city">
            {isManualMode ? "District / City" : "City"}
          </label>
          {isManualMode ? (
            <select
              id="city"
              name="city"
              value={form.city}
              onChange={handleChange}
              className="location-select"
            >
              <option value="" disabled>Select your district</option>
              {Array.from(new Set(cityList.map((c) => c.state))).map((state) => (
                <optgroup key={state} label={state}>
                  {cityList.filter((c) => c.state === state).map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          ) : (
            <input
              id="city"
              name="city"
              value={form.city}
              onChange={handleChange}
              className="frm-input"
            />
          )}
        </div>

        {/* Owner */}
        <div className="form-group">
          <label htmlFor="ownerName">Owner Name</label>
          <input
            id="ownerName"
            name="ownerName"
            value={form.ownerName}
            onChange={handleChange}
            className={`frm-input ${errors.ownerName ? "error" : ""}`}
          />
          {errors.ownerName && <span className="field-error">{errors.ownerName}</span>}
        </div>

        {/* Phone + OTP Verification */}
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>

          {/* Phone input row — input + verify/verified button side by side */}
          <div className="phone-verify-row">
            <input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handlePhoneChange}
              placeholder="Mobile number"
              disabled={mobileVerified}
              className={`frm-input ${errors.phone ? "error" : ""}`}
            />

            {/* Verify button — shown when OTP is enabled and not yet verified */}
            {otpEnabled && !mobileVerified && (
              <button
                type="button"
                className="otp-verify-btn"
                onClick={handleSendOtp}
                disabled={otpLoading}
              >
                {otpLoading && !otpSent ? (
                  "Sending..."
                ) : (
                  <>
                    <span className="btn-label-desktop">Verify Mobile</span>
                    <span className="btn-label-mobile">Verify</span>
                  </>
                )}
              </button>
            )}

            {/* Verified state indicator */}
            {mobileVerified && (
              <span className="otp-verified-badge">&#10003; Verified</span>
            )}
          </div>
          {!mobileVerified && (
            <span className="field-hint">Enter 10-digit number or include country code, e.g. +91</span>
          )}

          {/* OTP entry row — appears after OTP is sent */}
          {otpEnabled && otpSent && !mobileVerified && (
            <div className="otp-entry-row">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={(e) => {
                  setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setOtpError("");
                }}
                placeholder="Enter OTP"
                className="frm-input otp-input"
              />
              <button
                type="button"
                className="otp-confirm-btn"
                onClick={handleVerifyOtp}
                disabled={otpLoading}
              >
                {otpLoading ? "Verifying..." : "Confirm"}
              </button>
              <button
                type="button"
                className="otp-resend-btn"
                onClick={handleSendOtp}
                disabled={resendCountdown > 0 || otpLoading}
              >
                {otpLoading ? "Sending..." : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend"}
              </button>
            </div>
          )}

          {/* OTP error message */}
          {otpError && <span className="field-error">{otpError}</span>}

          {/* Form validation error for phone (includes mandatory OTP message) */}
          {errors.phone && <span className="field-error">{errors.phone}</span>}
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">Email <span className="field-optional">(Optional)</span></label>
          <input
            id="email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className={`frm-input ${errors.email ? "error" : ""}`}
          />
          {errors.email && <span className="field-error">{errors.email}</span>}
        </div>

        {/* Website */}
        <div className="form-group">
          <label htmlFor="website">Website <span className="field-optional">(Optional)</span></label>
          <input
            id="website"
            type="text"
            name="website"
            value={form.website}
            onChange={handleChange}
            placeholder="https://example.com"
            className={`frm-input ${errors.website ? "error" : ""}`}
          />
          {errors.website && <span className="field-error">{errors.website}</span>}
        </div>

        {/* Address */}
        <div className="form-group">
          <label htmlFor="address">Business Address <span className="field-optional">(Optional)</span></label>
          <input
            id="address"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Street, area, landmark"
            className={`frm-input ${errors.address ? "error" : ""}`}
          />
          {errors.address && <span className="field-error">{errors.address}</span>}
        </div>

        {/* Description */}
        <div className="form-group">
          <label htmlFor="description">About Business <span className="field-optional">(Optional)</span></label>
          <textarea
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="What makes your business unique?"
            className={`frm-input ${errors.description ? "error" : ""}`}
          />
          {errors.description && <span className="field-error">{errors.description}</span>}
        </div>

        {/* Images */}
        <div className="form-group">
          <label>Upload clear images. Keep subject centered (Max 3)</label>

          <input type="file" accept="image/*" multiple onChange={handleImageFile} id="fileUpload" disabled={imageLoading} style={{ display: "none" }} />
          <label htmlFor="fileUpload" className="upload-btn" style={imageLoading ? { opacity: 0.6, pointerEvents: "none" } : {}}>
            <span className="icon-row">
              <IconUpload size={15} />
              {imageLoading ? "Compressing..." : "Upload Images"}
            </span>
          </label>
          <span className="hint-text">{imageLoading ? "Optimising images, please wait…" : "Max 3 images allowed"}</span>

          <div className="addbiz-preview-container">
            {previews.map((img, index) => (
              <div key={index} className="addbiz-preview-box">
                <img
                  src={img}
                  alt="Business preview"
                  className="addbiz-preview-img"
                  loading="lazy"
                  decoding="async"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  style={{
                    position: "absolute",
                    top: "-1px",
                    right: "-1px",
                    background: "red",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    cursor: "pointer",
                    zIndex: 2
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="form-group">
          <label>Location</label>
          <p className="location-text">
            <span className="icon-row">
              <IconPin size={13} />
              {coords ? "Location detected" : isManualMode ? "Using entered city / address" : "Detecting location..."}
            </span>
          </p>
        </div>

        <button
          type="submit"
          className="search-btn"
          disabled={loading}
        >
          {loading ? "Registering..." : "Register Business"}
        </button>

      </form>
    </div>
  );
};

export default AddBusiness;
